import {
  forwardRef, useCallback, useEffect, useImperativeHandle,
  useMemo, useRef, useState,
} from "react";
import api, { extractApiError } from "@/lib/api";
import { usePreferences } from "@/lib/preferences";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Slider } from "@/components/ui/slider";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { HelpCircle, MessageCircleQuestion, Sparkles, Loader2 } from "lucide-react";
import { ReadAloud } from "@/components/TTS";
import { VoiceInputButton, appendTranscript } from "@/components/VoiceInputButton";

/**
 * AssessmentQuestion — renders a single question by type + wires up:
 *   - autosave (debounced onChange for text; immediate onChange for radio/multi/scale)
 *   - "Why we're asking" expander
 *   - AI helpers (explain simply / example) via /assessment/helper
 *   - TTS
 *   - `flush()` handle so the parent can force-save pending text before
 *     advancing to the next question or unmounting.
 *
 * Types supported: text, single, multi, scale, strengths_with_examples
 */
export const AssessmentQuestion = forwardRef(function AssessmentQuestion(
  { question, answer, skipped, onSave, onSkip },
  ref,
) {
  const { prefs } = usePreferences();
  const usePlain = !!prefs?.plain_language && question.plain_language;
  const primaryText = usePlain ? question.plain_language : question.prompt;

  const [showWhy, setShowWhy] = useState(false);
  const [localValue, setLocalValue] = useState(answer);
  const [helperOpen, setHelperOpen] = useState(false);
  const [helperContent, setHelperContent] = useState(null);
  const [helperKind, setHelperKind] = useState(null);
  const [helperLoading, setHelperLoading] = useState(false);
  const [helperError, setHelperError] = useState(null);

  // Refs used by flush / unmount cleanup so we always see the LATEST values
  // (avoids stale closures when the parent re-renders us with new props).
  const timerRef = useRef(null);
  const dirtyRef = useRef(false);
  const localValueRef = useRef(localValue);
  const onSaveRef = useRef(onSave);
  const questionIdRef = useRef(question.id);

  useEffect(() => { localValueRef.current = localValue; }, [localValue]);
  useEffect(() => { onSaveRef.current = onSave; }, [onSave]);
  useEffect(() => { questionIdRef.current = question.id; }, [question.id]);

  const isTextType =
    question.type === "text" || question.type === "strengths_with_examples";

  // When the question changes, reset local state cleanly.
  useEffect(() => {
    setLocalValue(answer);
    dirtyRef.current = false;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setHelperOpen(false);
    setHelperContent(null);
    setHelperError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question.id]);

  // When parent hands us a NEW answer prop for the SAME question (e.g. after
  // a background save) and we have no dirty pending value, sync in.
  useEffect(() => {
    if (!dirtyRef.current) setLocalValue(answer);
  }, [answer]);

  /**
   * flush() — cancel any pending debounce and immediately save if dirty.
   * Awaitable. Safe to call multiple times (no-op when clean).
   */
  const flush = useCallback(async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (dirtyRef.current) {
      dirtyRef.current = false;
      try {
        await onSaveRef.current(localValueRef.current, false);
      } catch (_e) {
        // parent handles UI/error state; we don't rethrow
      }
    }
  }, []);

  useImperativeHandle(ref, () => ({
    flush,
    isDirty: () => dirtyRef.current,
    /**
     * Returns the LIVE input value from local state — this is what the
     * user has actually typed, including text that hasn't been flushed
     * through the 700 ms debounce yet. The parent uses this in the
     * Next-button handler so that "type + immediate Next" does NOT
     * trigger the "Leave this unanswered?" modal.
     */
    getLiveValue: () => localValueRef.current,
    isEmptyLive: () => {
      const v = localValueRef.current;
      if (v === null || v === undefined) return true;
      if (typeof v === "string" && v.trim() === "") return true;
      if (Array.isArray(v) && v.length === 0) return true;
      return false;
    },
  }), [flush]);

  // On unmount (e.g. user clicks a nav link), best-effort fire-and-forget save.
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (dirtyRef.current) {
        dirtyRef.current = false;
        try {
          const p = onSaveRef.current(localValueRef.current, false);
          if (p && typeof p.catch === "function") p.catch(() => {});
        } catch (_e) { /* swallow */ }
      }
    };
  }, []);

  // beforeunload warning if there is unsaved text at page close/refresh.
  useEffect(() => {
    const handler = (e) => {
      if (dirtyRef.current) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  const onLocalChange = useCallback((value) => {
    setLocalValue(value);
    if (isTextType) {
      dirtyRef.current = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        dirtyRef.current = false;
        // Fire-and-forget debounced save. Any rejection is contained by the
        // parent saveAnswer (sets Save-failed indicator + persists local
        // draft to localStorage) — we STILL add .catch here so that if
        // anyone ever refactors saveAnswer to rethrow, we do not leak an
        // unhandled promise rejection to the browser (which used to
        // trigger the CRA error overlay on Q37).
        const p = onSaveRef.current(localValueRef.current, false);
        if (p && typeof p.catch === "function") p.catch(() => {});
      }, 700);
    } else {
      // Non-text: save immediately (radio, checkbox, scale).
      const p = onSaveRef.current(value, false);
      if (p && typeof p.catch === "function") p.catch(() => {});
    }
  }, [isTextType]);

  const askHelper = async (helper_type) => {
    setHelperOpen(true);
    setHelperKind(helper_type);
    setHelperLoading(true);
    setHelperError(null);
    setHelperContent(null);
    try {
      const { data } = await api.post("/assessment/helper", {
        question_id: question.id,
        helper_type,
      });
      setHelperContent(data);
    } catch (err) {
      setHelperError(extractApiError(err, "AI helper unavailable right now."));
    } finally {
      setHelperLoading(false);
    }
  };

  return (
    <div className="rounded-lg border border-border-subtle bg-bg-surface p-6" data-testid={`question-${question.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <p data-testid={`question-${question.id}-prompt`} className="text-lg text-fg-primary leading-relaxed font-heading">
            {primaryText}
          </p>
          {usePlain && (
            <p className="text-xs text-fg-secondary mt-1">Shown in plain language</p>
          )}
        </div>
      </div>

      {/* Actions row: read aloud + why + helpers */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {prefs?.read_aloud && (
          <ReadAloud text={primaryText} testidPrefix={`question-${question.id}-tts`} />
        )}
        {!prefs?.read_aloud && (
          <a
            href="/settings#voice"
            data-testid={`question-${question.id}-voice-help-link`}
            className="inline-flex items-center gap-1 text-xs text-fg-secondary hover:text-fg-primary underline"
            title="Read-aloud and microphone answering are opt-in. Enable them under Settings → Voice & reading."
          >
            Need read-aloud or microphone answering? Enable in Settings.
          </a>
        )}
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setShowWhy((v) => !v)}
          data-testid={`question-${question.id}-why-toggle`}
          className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"
        >
          <HelpCircle className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
          {showWhy ? "Hide" : "Why are you asking this?"}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => askHelper("explain_simply")}
          disabled={helperLoading}
          data-testid={`question-${question.id}-helper-explain`}
          className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover disabled:opacity-50"
        >
          <MessageCircleQuestion className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
          Explain more simply
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => askHelper("example")}
          disabled={helperLoading}
          data-testid={`question-${question.id}-helper-example`}
          className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover disabled:opacity-50"
        >
          <Sparkles className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
          Give me an example
        </Button>
      </div>

      {showWhy && (
        <div className="mt-3 rounded-md bg-bg-base border border-border-subtle p-3" data-testid={`question-${question.id}-why`}>
          <p className="text-sm text-fg-secondary">{question.why_asking}</p>
        </div>
      )}

      {helperOpen && (
        <div className="mt-3 rounded-md bg-progress-teal-bg/30 border border-progress-teal/40 p-3" data-testid={`question-${question.id}-helper-panel`}>
          {helperLoading && (
            <div className="flex items-center gap-2 text-sm text-fg-secondary">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              Asking the assistant…
            </div>
          )}
          {helperError && (
            <Alert variant="destructive" data-testid={`question-${question.id}-helper-error`}>
              <AlertDescription>{helperError}</AlertDescription>
            </Alert>
          )}
          {helperContent && (
            <>
              <p className="text-xs uppercase tracking-wider text-progress-teal mb-2">
                {helperKind === "example" ? "AI example" : "AI plain-language explanation"}
              </p>
              <p className="text-sm text-fg-primary whitespace-pre-line" data-testid={`question-${question.id}-helper-text`}>
                {helperContent.content}
              </p>
              <p className="text-xs text-fg-secondary mt-2">
                {helperContent.disclaimer}
              </p>
            </>
          )}
        </div>
      )}

      {/* Input area per type */}
      <div className="mt-6">
        <QuestionInput
          question={question}
          value={localValue}
          onChange={onLocalChange}
        />
      </div>

      {/* Skip control */}
      {question.safe_to_skip && (
        <div className="mt-4 flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onSkip(!skipped)}
            data-testid={`question-${question.id}-skip`}
            className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"
          >
            {skipped ? "I've changed my mind — I want to answer this" : "I'm not sure — skip this one"}
          </Button>
          {skipped && (
            <span className="text-xs text-fg-secondary">This question is marked skipped.</span>
          )}
        </div>
      )}
    </div>
  );
});

function QuestionInput({ question, value, onChange }) {
  if (question.type === "text") {
    return (
      <div>
        <Label htmlFor={question.id} className="sr-only">{question.prompt}</Label>
        <Textarea
          id={question.id}
          rows={5}
          data-testid={`question-${question.id}-text-input`}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => { /* onChange already scheduled save */ }}
          placeholder={question.placeholder || "Write in your own words…"}
          className="bg-bg-base border-border-subtle text-fg-primary"
        />
        <div className="mt-2">
          <VoiceInputButton
            fieldTestid={`question-${question.id}-voice-btn`}
            ariaLabel="Speak your answer"
            onTranscript={(t) => onChange(appendTranscript(value ?? "", t))}
          />
        </div>
      </div>
    );
  }

  if (question.type === "single") {
    return (
      <RadioGroup
        value={value ?? ""}
        onValueChange={(v) => onChange(v)}
        data-testid={`question-${question.id}-single`}
        className="space-y-2"
      >
        {question.options.map((opt) => (
          <label
            key={opt.value}
            className="flex items-start gap-3 p-3 border border-border-subtle rounded-md hover:bg-bg-surface-hover transition-colors duration-200 cursor-pointer"
          >
            <RadioGroupItem
              value={opt.value}
              id={`${question.id}-${opt.value}`}
              data-testid={`question-${question.id}-single-${opt.value}`}
              className="mt-0.5 border-border-subtle text-accent-warm"
            />
            <span className="text-sm text-fg-primary leading-relaxed">{opt.label}</span>
          </label>
        ))}
      </RadioGroup>
    );
  }

  if (question.type === "multi") {
    const selected = Array.isArray(value) ? value : [];
    const toggle = (v) => {
      onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
    };
    return (
      <div className="space-y-2" data-testid={`question-${question.id}-multi`}>
        {question.options.map((opt) => (
          <label
            key={opt.value}
            className="flex items-start gap-3 p-3 border border-border-subtle rounded-md hover:bg-bg-surface-hover transition-colors duration-200 cursor-pointer"
          >
            <Checkbox
              checked={selected.includes(opt.value)}
              onCheckedChange={() => toggle(opt.value)}
              data-testid={`question-${question.id}-multi-${opt.value}`}
              className="mt-0.5"
            />
            <span className="text-sm text-fg-primary leading-relaxed">{opt.label}</span>
          </label>
        ))}
      </div>
    );
  }

  if (question.type === "scale") {
    const min = question.scale_min ?? 1;
    const max = question.scale_max ?? 5;
    const num = typeof value === "number" ? value : null;
    return (
      <div className="space-y-3" data-testid={`question-${question.id}-scale`}>
        <div className="flex items-center justify-between text-xs text-fg-secondary">
          <span>{question.scale_min_label}</span>
          <span>{question.scale_max_label}</span>
        </div>
        <Slider
          min={min}
          max={max}
          step={1}
          value={[num ?? Math.round((min + max) / 2)]}
          onValueChange={([v]) => onChange(v)}
          className="[&_[role=slider]]:bg-accent-warm [&_[role=slider]]:border-accent-warm"
          data-testid={`question-${question.id}-scale-slider`}
        />
        <div className="flex justify-between text-xs">
          {Array.from({ length: max - min + 1 }, (_, i) => {
            const v = min + i;
            const active = num === v;
            return (
              <button
                key={v}
                type="button"
                onClick={() => onChange(v)}
                data-testid={`question-${question.id}-scale-${v}`}
                className={`h-8 w-8 rounded-full border ${active ? "bg-accent-warm text-bg-base border-accent-warm" : "border-border-subtle text-fg-secondary hover:bg-bg-surface-hover"}`}
              >
                {v}
              </button>
            );
          })}
        </div>
        {num !== null && (
          <p className="text-xs text-fg-secondary">You picked <strong className="text-fg-primary">{num}</strong>.</p>
        )}
      </div>
    );
  }

  if (question.type === "strengths_with_examples") {
    return <StrengthsWithExamples question={question} value={value} onChange={onChange} />;
  }

  return <p className="text-sm text-destructive">Unsupported question type: {question.type}</p>;
}

function StrengthsWithExamples({ question, value, onChange }) {
  const selections = useMemo(() => {
    if (!Array.isArray(value)) return [];
    return value;
  }, [value]);

  const byId = new Map(selections.map((s) => [s.strength, s]));

  const toggle = (id) => {
    if (byId.has(id)) {
      onChange(selections.filter((s) => s.strength !== id));
    } else {
      onChange([...selections, { strength: id, example: "" }]);
    }
  };

  const updateExample = (id, example) => {
    onChange(selections.map((s) => (s.strength === id ? { ...s, example } : s)));
  };

  return (
    <div className="space-y-3" data-testid={`question-${question.id}-strengths`}>
      <p className="text-xs text-fg-secondary">
        For each strength you pick, please share a real example. Strengths
        without a real example are noted as weaker evidence.
      </p>
      {question.strengths.map((s) => {
        const picked = byId.get(s.id);
        return (
          <div
            key={s.id}
            data-testid={`question-${question.id}-strength-${s.id}`}
            className={`rounded-md border p-3 transition-colors duration-200 ${picked ? "border-accent-warm/50 bg-bg-base" : "border-border-subtle hover:bg-bg-surface-hover"}`}
          >
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox
                checked={!!picked}
                onCheckedChange={() => toggle(s.id)}
                data-testid={`question-${question.id}-strength-${s.id}-toggle`}
                className="mt-0.5"
              />
              <span className="text-sm text-fg-primary leading-relaxed">{s.label}</span>
            </label>
            {picked && (
              <div className="mt-3 pl-8">
                <Label className="text-xs text-fg-secondary" htmlFor={`${question.id}-${s.id}-ex`}>
                  A time this showed up
                </Label>
                <Textarea
                  id={`${question.id}-${s.id}-ex`}
                  rows={2}
                  value={picked.example ?? ""}
                  onChange={(e) => updateExample(s.id, e.target.value)}
                  placeholder="A specific situation, in your own words…"
                  data-testid={`question-${question.id}-strength-${s.id}-example`}
                  className="mt-1 bg-bg-base border-border-subtle text-fg-primary"
                />
                <div className="mt-2">
                  <VoiceInputButton
                    fieldTestid={`question-${question.id}-strength-${s.id}-voice-btn`}
                    ariaLabel="Speak your example"
                    onTranscript={(t) => updateExample(s.id,
                      appendTranscript(picked.example ?? "", t))}
                  />
                </div>
                {(!picked.example || !picked.example.trim()) && (
                  <p className="mt-1 text-xs text-fg-muted">
                    Weaker evidence without an example.
                  </p>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default AssessmentQuestion;
